import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Save, Copy, Clock, Truck, ShoppingBag } from "lucide-react";
import { SchedulingConfigCard } from "./SchedulingConfigCard";

const DAYS = ["lunes", "martes", "miercoles", "jueves", "viernes", "sabado", "domingo"];
const DEFAULT_HOURS = DAYS.map(day => ({
  day,
  enabled: day !== "lunes",
  slots: [["12:00", "15:00"], ["19:00", "23:00"], ["", ""]],
}));

const DEFAULT_SCHEDULING = {
  mode: "allow", // "disabled", "allow", "forced"
  allow_when_open: true,
  hours_mode: "business_hours", // "business_hours" or "custom"
  min_anticipation_hours: 0,
  agenda_days: 14,
  interval_minutes: 30,
};

export function HorariosTab({ authAxios, restaurant, onUpdate }) {
  const [hours, setHours] = useState(DEFAULT_HOURS);
  const [waitMin, setWaitMin] = useState(40);
  const [waitMax, setWaitMax] = useState(40);
  const [scheduling, setScheduling] = useState(DEFAULT_SCHEDULING);
  const [saving, setSaving] = useState(false);
  const [deliveryEnabled, setDeliveryEnabled] = useState(true);
  const [pickupEnabled, setPickupEnabled] = useState(true);
  const [togglingService, setTogglingService] = useState(null);

  useEffect(() => {
    if (restaurant?.business_hours?.length > 0) {
      setHours(restaurant.business_hours.map(h => ({
        ...h,
        slots: [...(h.slots || []), ["", ""], ["", ""]].slice(0, 3),
      })));
    }
    if (restaurant?.wait_time_min) setWaitMin(restaurant.wait_time_min);
    if (restaurant?.wait_time_max) setWaitMax(restaurant.wait_time_max);
    if (restaurant?.scheduling_config) {
      setScheduling(prev => ({ ...prev, ...restaurant.scheduling_config }));
    }
    setDeliveryEnabled(restaurant?.delivery_enabled !== false);
    setPickupEnabled(restaurant?.pickup_enabled !== false);
  }, [restaurant]);

  const toggleService = async (field, value) => {
    setTogglingService(field);
    try {
      await authAxios.patch("/restaurant", { [field]: value });
      if (field === "delivery_enabled") setDeliveryEnabled(value);
      else setPickupEnabled(value);
      toast.success(value ? "Servico ativado" : "Servico desativado");
      if (onUpdate) onUpdate();
    } catch {
      toast.error("Erro ao atualizar servico");
    }
    setTogglingService(null);
  };

  const updateSlot = (dayIdx, slotIdx, pos, value) => {
    setHours(prev => prev.map((h, i) => {
      if (i !== dayIdx) return h;
      const newSlots = h.slots.map((s, si) => si === slotIdx ? (pos === 0 ? [value, s[1]] : [s[0], value]) : s);
      return { ...h, slots: newSlots };
    }));
  };

  const toggleDay = (dayIdx) => {
    setHours(prev => prev.map((h, i) => i === dayIdx ? { ...h, enabled: !h.enabled } : h));
  };

  const copyToNext = (dayIdx) => {
    const source = hours[dayIdx];
    const nextIdx = (dayIdx + 1) % 7;
    setHours(prev => prev.map((h, i) => i === nextIdx ? { ...h, slots: source.slots.map(s => [...s]), enabled: source.enabled } : h));
    toast.success(`Copiado de ${source.day} a ${hours[nextIdx].day}`);
  };

  const copyToAll = (dayIdx) => {
    const source = hours[dayIdx];
    setHours(prev => prev.map((h, i) => i === dayIdx ? h : { ...h, slots: source.slots.map(s => [...s]), enabled: source.enabled }));
    toast.success(`Horario de ${source.day} copiado a todos los dias`);
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      const cleanHours = hours.map(h => ({
        day: h.day,
        enabled: h.enabled,
        slots: h.slots.filter(s => s[0] && s[1]),
      }));
      await authAxios.patch("/restaurant", {
        business_hours: cleanHours,
        wait_time_min: waitMin,
        wait_time_max: waitMax,
        scheduling_config: scheduling,
      });
      toast.success("Configuracion guardada");
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error("Error guardando configuracion");
    }
    setSaving(false);
  };

  return (
    <>
      {/* Quick Service Toggles */}
      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="uppercase flex items-center gap-2"><Truck className="w-5 h-5" />Estado dos Servicos</CardTitle>
          <CardDescription>Ative ou desative entrega e retirada sem fechar o restaurante. Util quando nao ha estafeta disponivel.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between gap-4 p-4 rounded-xl border border-border/50 bg-secondary/30">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${deliveryEnabled ? "bg-green-100" : "bg-gray-100"}`}>
                <Truck className={`w-5 h-5 ${deliveryEnabled ? "text-green-600" : "text-gray-400"}`} />
              </div>
              <div>
                <p className="font-semibold text-sm">Delivery / Entrega</p>
                <p className="text-xs text-muted-foreground">{deliveryEnabled ? "Aceitando pedidos para entrega" : "Entrega desativada — clientes so podem retirar"}</p>
              </div>
            </div>
            <Switch
              checked={deliveryEnabled}
              onCheckedChange={v => toggleService("delivery_enabled", v)}
              disabled={togglingService === "delivery_enabled"}
              data-testid="toggle-delivery"
            />
          </div>
          <div className="flex items-center justify-between gap-4 p-4 rounded-xl border border-border/50 bg-secondary/30">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${pickupEnabled ? "bg-green-100" : "bg-gray-100"}`}>
                <ShoppingBag className={`w-5 h-5 ${pickupEnabled ? "text-green-600" : "text-gray-400"}`} />
              </div>
              <div>
                <p className="font-semibold text-sm">Retirada no local / Takeaway</p>
                <p className="text-xs text-muted-foreground">{pickupEnabled ? "Aceitando pedidos para retirada" : "Retirada desativada"}</p>
              </div>
            </div>
            <Switch
              checked={pickupEnabled}
              onCheckedChange={v => toggleService("pickup_enabled", v)}
              disabled={togglingService === "pickup_enabled"}
              data-testid="toggle-pickup"
            />
          </div>
          {!deliveryEnabled && pickupEnabled && (
            <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              O site esta <strong>aberto</strong> — clientes podem fazer pedidos para retirada e reservas, mas sem entrega.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Business Hours */}
      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="uppercase flex items-center gap-2"><Clock className="w-5 h-5" />Horarios de Funcionamiento</CardTitle>
          <CardDescription>Configure los horarios en que su restaurante acepta pedidos. Hasta 3 turnos por dia.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="hidden md:grid grid-cols-12 gap-2 text-xs font-semibold text-muted-foreground uppercase mb-2 px-2">
            <div className="col-span-2">Dia</div>
            <div className="col-span-3 text-center">Turno 1</div>
            <div className="col-span-3 text-center">Turno 2</div>
            <div className="col-span-3 text-center">Turno 3</div>
            <div className="col-span-1"></div>
          </div>
          {hours.map((h, dayIdx) => (
            <div key={h.day} className={`md:grid md:grid-cols-12 gap-2 items-center py-3 border-b border-border/30 ${!h.enabled ? "opacity-40" : ""}`} data-testid={`hours-row-${h.day}`}>
              <div className="col-span-2 flex items-center gap-2 mb-2 md:mb-0">
                <input type="checkbox" checked={h.enabled} onChange={() => toggleDay(dayIdx)} className="w-4 h-4 accent-orange-500" data-testid={`hours-check-${h.day}`} />
                <span className="font-medium capitalize text-sm">{h.day}</span>
              </div>
              <div className="col-span-9 flex flex-wrap gap-2">
                {h.slots.map((slot, slotIdx) => (
                  <div key={slotIdx} className="flex items-center gap-1">
                    <Input type="time" value={slot[0]} onChange={e => updateSlot(dayIdx, slotIdx, 0, e.target.value)}
                      className="bg-secondary border-transparent h-9 text-sm w-[110px]" disabled={!h.enabled} data-testid={`hours-${h.day}-${slotIdx}-from`} />
                    <span className="text-muted-foreground text-xs">a</span>
                    <Input type="time" value={slot[1]} onChange={e => updateSlot(dayIdx, slotIdx, 1, e.target.value)}
                      className="bg-secondary border-transparent h-9 text-sm w-[110px]" disabled={!h.enabled} data-testid={`hours-${h.day}-${slotIdx}-to`} />
                  </div>
                ))}
              </div>
              <div className="col-span-1 flex gap-1 mt-1 md:mt-0">
                <button onClick={() => copyToNext(dayIdx)} className="text-xs text-primary hover:underline flex items-center gap-0.5" title="Copiar al dia siguiente" data-testid={`hours-copy-next-${h.day}`}>
                  <Copy className="w-3 h-3" />
                </button>
                <button onClick={() => copyToAll(dayIdx)} className="text-[10px] text-muted-foreground hover:text-primary" title="Copiar a todos" data-testid={`hours-copy-all-${h.day}`}>
                  todos
                </button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Wait Time */}
      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="uppercase">Tiempo de Espera</CardTitle>
          <CardDescription>El tiempo minimo y maximo de espera para informar al cliente.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>Espera minima</Label>
              <div className="flex items-center gap-2">
                <Input type="number" value={waitMin} onChange={e => setWaitMin(Number(e.target.value))} className="bg-secondary border-transparent w-24" data-testid="wait-time-min" />
                <span className="text-muted-foreground text-sm">Minutos</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Espera maxima</Label>
              <div className="flex items-center gap-2">
                <Input type="number" value={waitMax} onChange={e => setWaitMax(Number(e.target.value))} className="bg-secondary border-transparent w-24" data-testid="wait-time-max" />
                <span className="text-muted-foreground text-sm">Minutos</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Scheduling Configuration */}
      <SchedulingConfigCard scheduling={scheduling} setScheduling={setScheduling} />

      <Button onClick={saveAll} disabled={saving} className="glow-orange" data-testid="save-hours-btn">
        <Save className="w-4 h-4 mr-2" />{saving ? "Guardando..." : "Guardar Configuracion"}
      </Button>
    </>
  );
}
